/**
 * The Marketing tab's status banner (listing-marketing-hub BR-1, BR-2): an active listing is either
 * "Live on Resida only. Not marketed yet." or N posts live; a paused or closed listing gets a
 * neutral banner saying what still happens to its Resida page and email, and that posting is off.
 */
import { CheckCircle2, Megaphone, PauseCircle } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { postedLabel } from '../../../lib/listing-marketing';
import { Button } from '../../ui/button';

export type MarketingBannerState = 'not_marketed' | 'marketed' | 'paused' | 'closed';

interface MarketingStatusBannerProps {
  state: MarketingBannerState;
  /** Posted external post records. */
  postedCount: number;
  /** How many of the two tracked sites (Craigslist, Facebook Marketplace) have a posted record. */
  sitesPosted: number;
  /** Re-activates a paused listing (the header's Re-activate action); the paused banner links to it. */
  onReactivate?: () => void;
}

const TRACKED_SITES = 2;

export function MarketingStatusBanner({ state, postedCount, sitesPosted, onReactivate }: MarketingStatusBannerProps) {
  const tone =
    state === 'marketed' ? 'border-success/25 bg-success/10' : state === 'not_marketed' ? 'border-warning/25 bg-warning/10' : 'border-border bg-muted';
  const Icon = state === 'marketed' ? CheckCircle2 : state === 'not_marketed' ? Megaphone : PauseCircle;
  const iconTone = state === 'marketed' ? 'text-success' : state === 'not_marketed' ? 'text-warning' : 'text-muted-foreground';

  return (
    <div
      role="status"
      data-testid="marketing-status-banner"
      data-state={state}
      className={cn('flex items-start gap-3 rounded-lg border p-4 text-sm', tone)}
    >
      <Icon aria-hidden="true" className={cn('mt-0.5 h-5 w-5 shrink-0', iconTone)} />
      <div className="flex min-w-0 flex-col gap-1">
        {state === 'not_marketed' && (
          <>
            <p className="font-semibold text-warning">Live on Resida only. Not marketed yet.</p>
            <p className="text-foreground">
              Publishing created your Resida listing page. Renters will not find it on their own. Post it where they
              search, then record each post here so Resida knows it is out there.
            </p>
            <p className="text-muted-foreground" data-testid="marketing-status-progress">
              {sitesPosted} of {TRACKED_SITES} tracked sites posted
            </p>
          </>
        )}
        {state === 'marketed' && (
          <>
            <p className="font-semibold text-success">{postedLabel(postedCount)}</p>
            <p className="text-foreground">
              Renters can find this listing beyond its Resida page. Post it on more sites below, and mark a post
              removed here when you take it down.
            </p>
          </>
        )}
        {state === 'paused' && (
          <>
            <p className="font-semibold text-foreground">This listing is paused.</p>
            <p className="text-foreground">
              Your Resida page stays up with a &quot;paused&quot; notice and no Apply or showing buttons. Anyone who
              emails your Resida address gets an automatic &quot;on hold&quot; reply. Posting is switched off until you
              re-activate.
            </p>
            {onReactivate && (
              <div>
                <Button
                  type="button"
                  variant="link"
                  className="h-auto min-h-[44px] px-0 text-primary underline"
                  data-testid="marketing-reactivate"
                  onClick={onReactivate}
                >
                  Re-activate listing
                </Button>
              </div>
            )}
          </>
        )}
        {state === 'closed' && (
          <>
            <p className="font-semibold text-foreground">This listing is closed.</p>
            <p className="text-foreground">
              Its Resida email no longer takes new inquiries, and posting is switched off. Reopen the listing to market
              it again.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
